import React from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  Edit,
  FileText,
  MessageCircle,
  Phone,
  Plus,
  Share2,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { ICONS as GLOBAL_ICONS } from "../../utils/icons";
import AddressLink from "../../components/AddressLink";
import { formatDateString } from "../../utils";
import { ExpandableText, FAITH_STATE_STYLES, LOCAL_ICONS } from "./counselingUiUtils";

export default function CounselingDetailModal({
  viewingStudent,
  isMobile,
  onTouchStart,
  onTouchMove,
  onModalSwipeEnd,
  setViewingStudent,
  setIsCreatingLog,
  visitLogs,
  attendanceLogs,
  setViewingLog,
  userProfile,
  detailModalPos,
  handleDetailMouseDown,
  theme,
  setSharingStudent,
  openAlert,
}) {
  if (!viewingStudent) return null;
  const isTeacher = viewingStudent.role && viewingStudent.role !== "학생";

  if (isMobile) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-white flex flex-col animate-slide-left overflow-hidden pb-[calc(56px+env(safe-area-inset-bottom))]"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={() => onModalSwipeEnd(() => setViewingStudent(null))}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-4 bg-white z-10 pt-[calc(env(safe-area-inset-top)+36px)] pb-3 border-b border-gray-100 min-h-[50px]">
          <button onClick={() => setViewingStudent(null)} className="text-gray-500 font-bold text-base">
            뒤로
          </button>
          <h2 className="font-bold text-lg text-gray-900">{viewingStudent.name}</h2>
          <button onClick={() => setIsCreatingLog(true)} className="font-bold text-base text-[#354F38]">
            기록
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#F2F4F6] pb-4">
          <div className="bg-white px-5 pb-8 pt-4 flex flex-col items-center border-b border-gray-100 rounded-b-[2rem] shadow-sm mb-4">
            <div className="w-24 h-24 rounded-[2rem] overflow-hidden shadow-lg border-[3px] border-white bg-gray-50 mb-4">
              {viewingStudent.photoUrl ? (
                <img src={viewingStudent.photoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-white p-2">
                  <img
                    src={
                      isTeacher
                        ? viewingStudent.gender === "여"
                          ? GLOBAL_ICONS.teacher_female_animated
                          : GLOBAL_ICONS.teacher_male_animated
                        : viewingStudent.gender === "남"
                          ? "/images/boy_animated_light.png"
                          : "/images/girl_animated_light.png"
                    }
                    alt="Profile"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">{viewingStudent.name}</h2>
            <p className="text-gray-500 font-bold text-sm bg-gray-100 px-3 py-1 rounded-full">
              {isTeacher
                ? `${viewingStudent.role || "교사"} • ${viewingStudent.charge || "미정"}`
                : `${viewingStudent.grade} ${viewingStudent.class ? `• ${viewingStudent.class}` : ""}`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 px-5 mb-6">
            <a
              href={`tel:${viewingStudent.phone}`}
              className="py-3.5 rounded-2xl bg-[#354F38] text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-[#354F38]/20 hover:bg-[#2A3F2D] hover:scale-[0.98] active:scale-[0.95] transition-all duration-200"
            >
              <Phone size={18} fill="currentColor" /> 전화
            </a>
            <a
              href={`sms:${viewingStudent.phone}`}
              className="py-3.5 rounded-2xl bg-[#FFECA1] text-[#4A3B10] font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-yellow-400/20 hover:bg-[#FFD54F] hover:scale-[0.98] active:scale-[0.95] transition-all duration-200"
            >
              <MessageCircle size={18} /> 문자
            </a>
          </div>

          <div className="px-5 space-y-4 pb-12">
            <h3 className="text-sm font-black text-gray-400 pl-1">상세 정보</h3>
            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-4">
              {!isTeacher && (
                <div className="flex justify-between items-center border-b border-gray-50 pb-3">
                  <span className="text-gray-400 font-bold text-sm">학교</span>
                  <span className="text-gray-800 font-bold">{viewingStudent.school || "-"}</span>
                </div>
              )}
              <div className="flex justify-between items-center border-b border-gray-50 pb-3">
                <span className="text-gray-400 font-bold text-sm">생년월일</span>
                <span className="text-gray-800 font-bold">{viewingStudent.birth ? formatDateString(viewingStudent.birth) : "-"}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-50 pb-3">
                <span className="text-gray-400 font-bold text-sm">연락처</span>
                <span className="text-gray-800 font-bold">{viewingStudent.phone || "-"}</span>
              </div>
              {!isTeacher && (viewingStudent.guardian1Name || viewingStudent.guardian1Phone) && (
                <div className="flex justify-between items-center border-b border-gray-50 pb-3">
                  <span className="text-gray-400 font-bold text-sm shrink-0">
                    보호자1 {viewingStudent.guardian1Name ? `(${viewingStudent.guardian1Name})` : ""}
                  </span>
                  <a href={`tel:${viewingStudent.guardian1Phone}`} className="text-[#354F38] font-bold underline underline-offset-2">
                    {viewingStudent.guardian1Phone}
                  </a>
                </div>
              )}
              <div className={`flex justify-between items-start ${!isTeacher ? "border-b border-gray-50 pb-3" : "pt-1"}`}>
                <span className="text-gray-400 font-bold text-sm shrink-0">주소</span>
                <AddressLink
                  address={viewingStudent.address}
                  className="text-gray-800 font-bold text-right text-sm leading-relaxed max-w-[200px] break-keep"
                />
              </div>
              {!isTeacher && (
                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-400 font-bold text-sm">신앙상태</span>
                  <span className={`font-bold px-2 py-0.5 rounded-md text-xs ${FAITH_STATE_STYLES[viewingStudent.faithState] || "bg-gray-50 text-gray-800"}`}>
                    {viewingStudent.faithState || "-"}
                  </span>
                </div>
              )}
            </div>

            <div className="px-1 space-y-4">
              <h3 className="text-sm font-black text-gray-400 mb-1 flex items-center gap-1">사역 타임라인</h3>
              <div className="bg-white/50 backdrop-blur-sm rounded-3xl p-5 border border-white shadow-sm -mx-1">
                <div className="space-y-6 relative">
                  <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-100"></div>

                  {(() => {
                    const history = [];

                    visitLogs
                      .filter((l) => l.studentId === viewingStudent.id)
                      .forEach((l) => {
                        history.push({
                          type: "visit",
                          date: new Date(l.date),
                          title:
                            l.type === "상담"
                              ? "마음을 나눴던 소중한 시간"
                              : l.type === "기도"
                                ? "함께 마음 모아 기도했습니다"
                                : "삶의 자리에서 함께했습니다",
                          content: l.content,
                          icon:
                            l.type === "상담" ? (
                              <FileText size={14} />
                            ) : l.type === "기도" ? (
                              <img src={LOCAL_ICONS.prayer} width={14} alt="pray" />
                            ) : (
                              <CheckCircle size={14} />
                            ),
                          color: l.type === "상담" ? "bg-blue-500" : l.type === "기도" ? "bg-indigo-500" : "bg-[#354F38]",
                          raw: l,
                        });
                      });

                    if (attendanceLogs) {
                      const sLogs = attendanceLogs
                        .filter((l) => l[viewingStudent.id])
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 5);
                      sLogs.forEach((l) => {
                        const status = l[viewingStudent.id];
                        if (status === "present" || status === "late") {
                          history.push({
                            type: "attendance",
                            date: new Date(l.date),
                            title: status === "present" ? "예배의 자리를 지켰습니다" : "조금 늦었지만, 함께해서 기쁩니다",
                            icon: <Calendar size={14} />,
                            color: status === "present" ? "bg-emerald-500" : "bg-amber-500",
                          });
                        }
                      });
                    }

                    if (viewingStudent.createdAt) {
                      history.push({
                        type: "system",
                        date: viewingStudent.createdAt.toDate ? viewingStudent.createdAt.toDate() : new Date(viewingStudent.createdAt),
                        title: isTeacher ? "The STAFF 교사 등록" : "The STAFF 부원 등록",
                        icon: <Plus size={14} />,
                        color: "bg-gray-800",
                      });
                    }

                    const sortedHistory = history.sort((a, b) => b.date - a.date);
                    if (sortedHistory.length === 0) {
                      return (
                        <div className="py-8 flex flex-col items-center gap-3 opacity-40">
                          <img src={LOCAL_ICONS.memo} width={32} className="grayscale" alt="empty" />
                          <p className="text-xs font-bold text-gray-400">기록이 없습니다</p>
                        </div>
                      );
                    }

                    return sortedHistory.map((item, idx) => (
                      <div key={idx} className="relative pl-10">
                        <div className={`absolute left-0 top-0.5 w-8 h-8 rounded-2xl ${item.color} shadow-lg z-10 flex items-center justify-center text-white scale-90`}>
                          {item.icon}
                        </div>
                        <div className="bg-white/60 p-3.5 rounded-2xl border border-gray-50">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-black text-gray-300 uppercase">
                              {item.date.toLocaleDateString("ko-KR", { year: "2-digit", month: "short", day: "numeric" })}
                            </span>
                            <h4 className="text-sm font-black text-gray-700">{item.title}</h4>
                            {item.content && <ExpandableText text={item.content} maxLines={2} />}
                            {item.type === "visit" && (
                              <button onClick={() => setViewingLog(item.raw)} className="text-[10px] font-black text-[#354F38] mt-2 flex items-center gap-1">
                                상세 보기 <ArrowRight size={10} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {viewingStudent.notes && (
              <div className="bg-yellow-50/50 rounded-3xl p-5 shadow-sm border border-yellow-100/50 mt-4">
                <h4 className="text-xs font-black text-yellow-600/80 mb-2 flex items-center gap-1">
                  <StickyNote size={12} /> 비고
                </h4>
                <p className="text-sm font-medium text-gray-700 leading-relaxed">
                  {isTeacher && userProfile?.memberRole === "teacher" && userProfile?.uid !== viewingStudent.linkedUserId ? (
                    <span className="blur-[4px] select-none text-gray-400">내용 보호 중</span>
                  ) : (
                    viewingStudent.notes
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[1000] p-4 pointer-events-none">
      <div
        className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl relative overflow-hidden border border-white/50 h-[85vh] max-h-[90vh] flex flex-col pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(${detailModalPos.x}px, ${detailModalPos.y}px)` }}
      >
        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 z-20 shrink-0 cursor-move bg-white" onMouseDown={handleDetailMouseDown}>
          <h3 className="text-xl font-black text-[#2C3E30] flex items-center gap-2 font-do-hyeon">
            <img src={isTeacher ? LOCAL_ICONS.teacher : LOCAL_ICONS.student} width={28} alt="icon" /> {isTeacher ? "교사 상세 정보" : "학생 상세 정보"}
          </h3>
          <button
            onClick={() => setViewingStudent(null)}
            className="text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="w-full h-full overflow-y-auto custom-scrollbar p-0">
          <div className="px-8 pb-24 pt-8 relative z-10 w-full flex flex-col">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="relative mb-4">
                <div className="w-28 h-28 rounded-[2.5rem] overflow-hidden shadow-lg border-4 border-white bg-white">
                  {viewingStudent.photoUrl ? (
                    <img src={viewingStudent.photoUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white p-2">
                      <img
                        src={
                          isTeacher
                            ? viewingStudent.gender === "여"
                              ? GLOBAL_ICONS.teacher_female_animated
                              : GLOBAL_ICONS.teacher_male_animated
                            : viewingStudent.gender === "남"
                              ? "/images/boy_animated_light.png"
                              : "/images/girl_animated_light.png"
                        }
                        alt="Profile"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                </div>
                {!isTeacher && viewingStudent.faithState === "초신자" && (
                  <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-white text-xs font-bold px-3 py-1.5 rounded-full border-[3px] border-white shadow-sm animate-pulse">
                    NEW
                  </div>
                )}
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-1">{viewingStudent.name}</h2>
              <div className="flex flex-col items-center gap-2">
                {isTeacher ? (
                  <p className="text-gray-500 font-medium text-lg">
                    {viewingStudent.role || "교사"} {viewingStudent.charge && `• ${viewingStudent.charge}`}
                  </p>
                ) : (
                  <p className="text-gray-500 font-medium text-lg">
                    {viewingStudent.department || viewingStudent.grade} {viewingStudent.class && `• ${viewingStudent.class}`}
                  </p>
                )}
                {(() => {
                  const prayerLog = visitLogs
                    .filter((l) => l.studentId === viewingStudent.id && l.type === "기도")
                    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

                  if (!prayerLog) return null;

                  return (
                    <div className="flex items-center gap-2 bg-white/60 backdrop-blur-md px-4 py-2 rounded-xl border border-white/50 shadow-sm mt-1 animate-fade-in-up">
                      <img src={LOCAL_ICONS.prayer} width={20} alt="prayer" />
                      <p className="text-sm font-bold text-gray-700">"{prayerLog.content}"</p>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 w-full mb-10">
              <a
                href={`tel:${viewingStudent.phone}`}
                className={`py-4 rounded-3xl ${theme?.bg || "bg-[#354F38]"} text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg ${theme?.shadow || "shadow-[#354F38]/20"} hover:opacity-90 hover:scale-[0.98] active:scale-[0.95] transition-all duration-200`}
              >
                <Phone size={20} fill="currentColor" /> 전화
              </a>
              <a
                href={`sms:${viewingStudent.phone}`}
                className="py-4 rounded-3xl bg-[#FFECA1] text-[#4A3B10] font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-yellow-400/20 hover:bg-[#FFD54F] hover:scale-[0.98] active:scale-[0.95] transition-all duration-200"
              >
                <MessageCircle size={20} /> 문자
              </a>
            </div>

            <div className="w-full space-y-1 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 px-1">{isTeacher ? "교사 정보" : "학생 정보"}</h3>

              <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium text-base">생년월일</span>
                <span className="text-gray-900 font-bold text-xl">{viewingStudent.birth ? formatDateString(viewingStudent.birth) : "-"}</span>
              </div>
              <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium text-base">연락처</span>
                <span className="text-gray-900 font-bold text-xl">{viewingStudent.phone || "-"}</span>
              </div>
              {!isTeacher && (viewingStudent.guardian1Name || viewingStudent.guardian1Phone) && (
                <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                  <span className="text-gray-500 font-medium text-base">
                    보호자1 {viewingStudent.guardian1Name ? `(${viewingStudent.guardian1Name})` : ""}
                  </span>
                  <a href={`tel:${viewingStudent.guardian1Phone}`} className="text-[#354F38] font-bold text-xl underline underline-offset-2">
                    {viewingStudent.guardian1Phone}
                  </a>
                </div>
              )}
              {!isTeacher && (viewingStudent.guardian2Name || viewingStudent.guardian2Phone) && (
                <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                  <span className="text-gray-500 font-medium text-base">
                    보호자2 {viewingStudent.guardian2Name ? `(${viewingStudent.guardian2Name})` : ""}
                  </span>
                  <a href={`tel:${viewingStudent.guardian2Phone}`} className="text-[#354F38] font-bold text-xl underline underline-offset-2">
                    {viewingStudent.guardian2Phone}
                  </a>
                </div>
              )}
              <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium text-base">학교/직장</span>
                <span className="text-gray-900 font-bold text-xl">{viewingStudent.school || "-"}</span>
              </div>
              <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                <span className="text-gray-500 font-medium text-base">주소</span>
                <AddressLink address={viewingStudent.address} className="text-gray-900 font-bold text-lg text-right truncate pl-4" />
              </div>
              {!isTeacher && (
                <div className="flex justify-between items-center py-4 px-1 border-b border-gray-50">
                  <span className="text-gray-500 font-medium text-base">신앙상태</span>
                  <span className={`font-bold text-lg px-4 py-1.5 rounded-xl border min-w-[80px] text-center inline-block shadow-sm ${FAITH_STATE_STYLES[viewingStudent.faithState] || "bg-gray-50 text-gray-800 border-gray-100"}`}>
                    {viewingStudent.faithState || "-"}
                  </span>
                </div>
              )}
            </div>

            <div className="w-full space-y-1 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 px-1">사역 타임라인</h3>
              <div className="bg-[#F9FAFB]/50 rounded-[2rem] p-6 border border-gray-100 relative overflow-hidden">
                <div className="absolute left-10 top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#354F38]/20 via-gray-100 to-transparent"></div>

                <div className="space-y-8 relative">
                  {(() => {
                    const history = [];

                    visitLogs
                      .filter((l) => l.studentId === viewingStudent.id)
                      .forEach((l) => {
                        history.push({
                          type: "visit",
                          date: new Date(l.date),
                          title:
                            l.type === "상담"
                              ? "마음을 나눴던 소중한 시간"
                              : l.type === "기도"
                                ? "함께 마음 모아 기도했습니다"
                                : "삶의 자리에서 함께했습니다",
                          content: l.content,
                          icon:
                            l.type === "상담" ? (
                              <FileText size={16} />
                            ) : l.type === "기도" ? (
                              <img src={LOCAL_ICONS.prayer} width={16} alt="pray" />
                            ) : (
                              <CheckCircle size={16} />
                            ),
                          color: l.type === "상담" ? "bg-blue-500 shadow-blue-200" : l.type === "기도" ? "bg-indigo-500 shadow-indigo-200" : "bg-[#354F38] shadow-emerald-200",
                          raw: l,
                        });
                      });

                    if (attendanceLogs) {
                      const sLogs = attendanceLogs
                        .filter((l) => l[viewingStudent.id])
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 5);
                      sLogs.forEach((l) => {
                        const status = l[viewingStudent.id];
                        if (status === "present" || status === "late") {
                          history.push({
                            type: "attendance",
                            date: new Date(l.date),
                            title: status === "present" ? "예배의 자리를 지켰습니다" : "조금 늦었지만, 함께해서 기쁩니다",
                            icon: <Calendar size={16} />,
                            color: status === "present" ? "bg-emerald-500 shadow-emerald-200" : "bg-amber-500 shadow-amber-200",
                          });
                        }
                      });
                    }

                    if (viewingStudent.createdAt) {
                      history.push({
                        type: "system",
                        date: viewingStudent.createdAt.toDate ? viewingStudent.createdAt.toDate() : new Date(viewingStudent.createdAt),
                        title: isTeacher ? "The STAFF 교사 등록" : "The STAFF 부원 등록",
                        content: "우리 공동체와 함께한 시작일이에요! 🌱",
                        icon: <Plus size={16} />,
                        color: "bg-gray-800 shadow-gray-200",
                      });
                    }

                    const sortedHistory = history.sort((a, b) => b.date - a.date);
                    if (sortedHistory.length === 0) {
                      return (
                        <div className="py-12 flex flex-col items-center gap-4 opacity-50">
                          <img src={LOCAL_ICONS.memo} width={40} className="grayscale" alt="empty" />
                          <p className="text-sm font-bold text-gray-400">활동 기록이 비어있습니다</p>
                        </div>
                      );
                    }

                    return sortedHistory.map((item, idx) => (
                      <div key={idx} className="relative pl-12 group cursor-default transition-all duration-300 text-left">
                        <div className={`absolute left-0 top-1 w-8 h-8 rounded-2xl ${item.color} shadow-lg z-10 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300 ${idx === 0 ? "animate-pulse" : ""}`}>
                          {item.icon}
                        </div>

                        <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-4 border border-white hover:bg-white hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-black text-[#354F38]/40 uppercase tracking-widest">
                              {item.date.toLocaleDateString("ko-KR", { year: "2-digit", month: "long", day: "numeric" })}
                            </span>
                            <h4 className="text-sm font-black text-gray-800">{item.title}</h4>
                            {item.content && (
                              <p className="text-xs text-gray-500 font-medium leading-relaxed mt-1 break-keep p-3 bg-gray-50/50 rounded-xl border border-gray-100 line-clamp-3 group-hover:line-clamp-none transition-all duration-500">
                                {item.content}
                              </p>
                            )}
                            {item.type === "visit" && (
                              <button onClick={() => setViewingLog(item.raw)} className="text-[10px] font-bold text-[#354F38] mt-2 flex items-center gap-1 hover:underline">
                                상세 보기 <ArrowRight size={10} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            <div className="w-full mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 px-1">메모</h3>
              <div className="bg-[#FFF9E5] p-6 rounded-3xl text-gray-800 font-medium leading-relaxed shadow-sm text-left">
                {isTeacher && userProfile?.memberRole === "teacher" && userProfile?.uid !== viewingStudent.linkedUserId ? (
                  <span className="blur-[5px] select-none text-gray-400">메모 내용이 가려져 있습니다.</span>
                ) : (
                  viewingStudent.notes || "특이사항이 없습니다."
                )}
              </div>
            </div>

            <div className="flex justify-center gap-6 mt-4 pt-6 border-t border-gray-100">
              <button onClick={() => setSharingStudent(viewingStudent)} className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-800 transition-colors group">
                <div className="p-3 rounded-full bg-gray-50 group-hover:bg-gray-100 transition-colors">
                  <Share2 size={20} />
                </div>
                <span className="text-xs font-medium">공유</span>
              </button>
              {userProfile?.memberRole !== "teacher" && (
                <>
                  <button
                    onClick={() => {
                      openAlert("명단 관리 탭에서 수정 가능합니다.", "알림");
                    }}
                    className="flex flex-col items-center gap-1 text-gray-400 hover:text-blue-600 transition-colors group"
                  >
                    <div className="p-3 rounded-full bg-gray-50 group-hover:bg-blue-50 transition-colors">
                      <Edit size={20} />
                    </div>
                    <span className="text-xs font-medium">수정</span>
                  </button>
                  <button
                    onClick={() => {
                      openAlert("명단 관리 탭에서 삭제 가능합니다.", "알림");
                    }}
                    className="flex flex-col items-center gap-1 text-gray-400 hover:text-rose-600 transition-colors group"
                  >
                    <div className="p-3 rounded-full bg-gray-50 group-hover:bg-rose-50 transition-colors">
                      <Trash2 size={20} />
                    </div>
                    <span className="text-xs font-medium">삭제</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
